import { Clock } from 'lucide-react';
import type { DiasSemana } from '@shared/types';
import {
  hhmmDesdeMinutos,
  minutosDesdeHHMM,
  ventanaEnPalabras,
} from '@shared/ventanaDisponibilidad';

/**
 * Sprint 74 — "¿Cuándo se vende?": días de la semana y horas del día.
 *
 * ## Por qué es un componente compartido
 *
 * Porque la misma pregunta aparece en dos formularios y tiene que verse y comportarse igual: las
 * promociones y los paquetes. Dos de los seis carteles reales del negocio —*Promo Amigos* $599 y
 * *Gratis medio pollo* $299— no son promociones sino **paquetes con horario**, y el resto son
 * promociones con horario. Si cada formulario preguntara a su manera, la dueña tendría que aprender
 * dos veces lo mismo.
 *
 * ## El criterio de la pantalla
 *
 * Los carteles dicen *"válido de miércoles a viernes de 1PM a 5PM"*. La pantalla pregunta en ese
 * orden y con esas palabras, y **debajo repite la ventana completa en una frase** —"los miércoles,
 * jueves y viernes de 1:00 p.m. a 5:00 p.m."— para que se pueda comparar con el cartel de un
 * vistazo, sin traducir de 24 horas.
 *
 * El horario está apagado por default: la mayoría de las cosas se venden todo el día, y un campo de
 * hora vacío que hay que llenar sería una trampa.
 */

export interface ValorVentana {
  diasSemana: DiasSemana | null;
  horaInicio: number | null;
  horaFin: number | null;
}

interface Props {
  valor: ValorVentana;
  onChange: (v: ValorVentana) => void;
  /** "esta promoción" / "este paquete": el texto de ayuda habla de lo que se está creando. */
  queEs: string;
}

const DIAS_TODOS: DiasSemana = {
  lunes: true, martes: true, miercoles: true, jueves: true,
  viernes: true, sabado: true, domingo: true,
};

const DIAS_LABELS: Array<[keyof DiasSemana, string, string]> = [
  ['lunes', 'L', 'lunes'],
  ['martes', 'M', 'martes'],
  ['miercoles', 'X', 'miércoles'],
  ['jueves', 'J', 'jueves'],
  ['viernes', 'V', 'viernes'],
  ['sabado', 'S', 'sábado'],
  ['domingo', 'D', 'domingo'],
];

export function VentanaDisponibilidadFields({ valor, onChange, queEs }: Props) {
  const usaDias = valor.diasSemana !== null;
  const usaHoras = valor.horaInicio !== null || valor.horaFin !== null;
  const frase = ventanaEnPalabras(valor);

  const toggleDia = (k: keyof DiasSemana) => {
    const base = valor.diasSemana ?? DIAS_TODOS;
    onChange({ ...valor, diasSemana: { ...base, [k]: !base[k] } });
  };

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
      <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
        <Clock className="h-4 w-4" />
        ¿Cuándo se vende?
      </p>

      {/* ── Días ── */}
      <div>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={usaDias}
            onChange={(e) =>
              onChange({ ...valor, diasSemana: e.target.checked ? DIAS_TODOS : null })
            }
          />
          <span className="text-sm">Sólo ciertos días de la semana</span>
        </label>
        {usaDias && (
          <div className="mt-2 flex flex-wrap gap-2">
            {DIAS_LABELS.map(([k, corto, largo]) => (
              <button
                key={k}
                type="button"
                onClick={() => toggleDia(k)}
                // 44 px: es un botón para un dedo, no para un mouse.
                className={`h-11 w-11 rounded-full border text-sm font-semibold ${
                  (valor.diasSemana ?? DIAS_TODOS)[k]
                    ? 'border-primary bg-primary text-primary-fg'
                    : 'border-slate-300 bg-white text-slate-500'
                }`}
                aria-label={largo}
                aria-pressed={(valor.diasSemana ?? DIAS_TODOS)[k]}
              >
                {corto}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Horas ── */}
      <div>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={usaHoras}
            onChange={(e) =>
              /*
                Al prender, se propone 1 a 5 de la tarde: es la ventana de los carteles del negocio y
                evita dos campos vacíos que hay que adivinar. Al apagar, las dos horas se van a null
                juntas — con una sola, el motor ignoraría el horario y la dueña creería tenerlo puesto.
              */
              onChange(
                e.target.checked
                  ? { ...valor, horaInicio: 13 * 60, horaFin: 17 * 60 }
                  : { ...valor, horaInicio: null, horaFin: null },
              )
            }
          />
          <span className="text-sm">Sólo a ciertas horas</span>
        </label>
        {usaHoras && (
          <div className="mt-2 flex flex-wrap items-end gap-3">
            <div>
              <label className="label" htmlFor="ventanaDesde">Desde</label>
              <input
                id="ventanaDesde"
                type="time"
                className="input min-h-[44px]"
                value={hhmmDesdeMinutos(valor.horaInicio)}
                onChange={(e) =>
                  onChange({ ...valor, horaInicio: minutosDesdeHHMM(e.target.value) })
                }
              />
            </div>
            <div>
              <label className="label" htmlFor="ventanaHasta">Hasta</label>
              <input
                id="ventanaHasta"
                type="time"
                className="input min-h-[44px]"
                value={hhmmDesdeMinutos(valor.horaFin)}
                onChange={(e) => onChange({ ...valor, horaFin: minutosDesdeHHMM(e.target.value) })}
              />
            </div>
          </div>
        )}
      </div>

      {/*
        La ventana repetida en palabras. Es lo que permite comparar contra el cartel sin traducir de
        24 horas, y lo que hace visible el caso de la madrugada.
      */}
      {frase ? (
        <p className="rounded-md bg-white px-3 py-2 text-sm text-slate-700">
          Se vende <b>{frase}</b>.
        </p>
      ) : (
        <p className="rounded-md bg-white px-3 py-2 text-sm text-slate-500">
          Como está, {queEs} se aplica <b>todos los días, a toda hora</b>.
        </p>
      )}

      {valor.horaInicio !== null &&
        valor.horaFin !== null &&
        valor.horaFin <= valor.horaInicio && (
          <p className="rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-900">
            Cruza la medianoche, y está bien: se entiende que la madrugada pertenece al día que
            empezó. Un viernes de 10:00 p.m. a 2:00 a.m. sigue aplicando a la 1 de la mañana del
            sábado.
          </p>
        )}
    </div>
  );
}
